// pages/rental/RentalCheckout.jsx — Ink & Lime Design System (All-in-One)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';
import {
  AlertNotification,
  useCheckoutData,
  useCoupon,
  usePayment,
} from '../../components/rental/checkout';
import { formatDateOnly, formatTimeOnly } from '../../components/rental/checkout/utils/checkoutHelpers';

// ── Design Tokens ─────────────────────────────────────────────────────────
const T = {
  ink:     '#0D0D0F',
  ink2:    '#1A1A20',
  slate:   '#2D2D38',
  muted:   '#6B6B7A',
  line:    '#E8E8F0',
  surface: '#F5F5FA',
  white:   '#FFFFFF',
  lime:    '#C8F23C',
  limeD:   '#A8D020',
  teal:    '#00BFA5',
  coral:   '#FF6D3A',
};

// ── Shared Card Shell ──────────────────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div style={{
      background: T.white, borderRadius: 18,
      border: `1.5px solid ${T.line}`,
      overflow: 'hidden',
      boxShadow: '0 2px 12px rgba(0,0,0,.04)',
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardHead({ icon, title }) {
  return (
    <div style={{
      padding: '14px 20px',
      borderBottom: `1px solid ${T.line}`,
      display: 'flex', alignItems: 'center', gap: 8,
      background: T.surface,
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <h2 style={{ fontSize: 14, fontWeight: 800, color: T.ink, letterSpacing: .2 }}>{title}</h2>
    </div>
  );
}

// ── VehicleCard ───────────────────────────────────────────────────────────
function VehicleCard({ bookingData }) {
  const [imgErr, setImgErr] = useState(false);
  return (
    <Card>
      <CardHead icon="🏍️" title="Vehicle Details" />
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {/* Image */}
          <div style={{
            width: 160, height: 128, borderRadius: 14, overflow: 'hidden',
            background: T.slate, flexShrink: 0,
            border: `1.5px solid ${T.line}`,
          }}>
            {!imgErr && bookingData.bikeImage ? (
              <img src={bookingData.bikeImage} alt={bookingData.bikeName}
                onError={() => setImgErr(true)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🏍️</div>
            )}
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 180 }}>
            <h3 style={{ fontSize: 18, fontWeight: 900, color: T.ink, marginBottom: 12 }}>
              {bookingData.bikeName}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Reg. Number', value: bookingData.registrationNumber, icon: '🔖' },
                { label: 'Pickup Location', value: bookingData.storeName, icon: '📍' },
              ].map(({ label, value, icon }) => value ? (
                <div key={label} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(200,242,60,.1)', border: `1px solid rgba(200,242,60,.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: .8, textTransform: 'uppercase', marginBottom: 1 }}>{label}</p>
                    <p style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{value}</p>
                  </div>
                </div>
              ) : null)}

              {/* Package badge */}
              {bookingData.hasPackageSelected === 'true' && bookingData.packageName && (
                <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, background: T.ink, border: `1px solid ${T.lime}` }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.lime, display: 'inline-block' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.lime }}>{bookingData.packageName}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Available badge */}
        <div style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, background: 'rgba(0,191,165,.1)', border: `1px solid rgba(0,191,165,.3)` }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.teal, display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: T.teal }}>Available for Booking</span>
        </div>
      </div>
    </Card>
  );
}

// ── TripDetailsCard ───────────────────────────────────────────────────────
function TripDetailsCard({ bookingData, durationInfo }) {
  const Row = ({ icon, label, value }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <div>
        <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 1 }}>{label}</p>
        <p style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{value}</p>
      </div>
    </div>
  );

  return (
    <Card>
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>🗓️</span>
          <h2 style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>Trip Details</h2>
        </div>
        {durationInfo?.displayDuration && (
          <div style={{ padding: '4px 12px', borderRadius: 20, background: T.ink, color: T.lime, fontSize: 11, fontWeight: 800 }}>
            {durationInfo.displayDuration}
          </div>
        )}
      </div>

      <div style={{ padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {/* Pickup */}
          <div style={{ padding: '14px', borderRadius: 14, background: T.surface, border: `1.5px solid ${T.line}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🚀</div>
              <span style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>Pickup</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Row icon="📅" label="Date" value={formatDateOnly(bookingData.startDate)} />
              <Row icon="🕐" label="Time" value={formatTimeOnly(bookingData.pickupTime)} />
            </div>
          </div>

          {/* Dropoff */}
          <div style={{ padding: '14px', borderRadius: 14, background: T.ink, border: `1.5px solid ${T.lime}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>🏁</div>
              <span style={{ fontSize: 12, fontWeight: 800, color: T.white }}>Drop-off</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>📅</span>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .8, marginBottom: 1 }}>Date</p>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.lime }}>{formatDateOnly(bookingData.endDate)}</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>🕐</span>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: .8, marginBottom: 1 }}>Time</p>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.lime }}>{formatTimeOnly(bookingData.dropoffTime)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pickup mode */}
        <div style={{ padding: '12px 14px', borderRadius: 12, background: T.surface, border: `1px solid ${T.line}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
            {bookingData.pickupMode === 'delivery' ? '🚚' : '🏪'}
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 2 }}>Pickup Mode</p>
            <p style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>
              {bookingData.pickupMode === 'delivery' ? 'Home Delivery' : 'Self Pickup'}
            </p>
            {bookingData.pickupMode === 'delivery' && bookingData.deliveryAddress && (
              <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>📍 {bookingData.deliveryAddress}</p>
            )}
          </div>
        </div>

        {/* Info note */}
        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: 'rgba(200,242,60,.06)', border: '1px solid rgba(200,242,60,.2)', fontSize: 11, color: T.muted, display: 'flex', gap: 6 }}>
          <span style={{ flexShrink: 0 }}>ℹ️</span>
          Please arrive on time. Late arrivals may result in adjusted booking duration.
        </div>
      </div>
    </Card>
  );
}

// ── ImportantNotes ────────────────────────────────────────────────────────
const NOTES = [
  { icon: '🪪', text: 'Carry original driving license and Aadhaar card', em: true },
  { icon: '⏰', text: 'Vehicle must be returned on time to avoid late charges', em: false },
  { icon: '🪖', text: 'Helmet is mandatory for rider and pillion', em: false },
];

function ImportantNotes() {
  return (
    <Card>
      <CardHead icon="📋" title="Important Notes" />
      <div style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {NOTES.map((n, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(200,242,60,.12)', border: '1px solid rgba(200,242,60,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11 }}>✓</div>
              <div style={{ display: 'flex', gap: 7, flex: 1 }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{n.icon}</span>
                <p style={{ fontSize: 13, fontWeight: n.em ? 800 : 500, color: n.em ? T.ink : T.slate, lineHeight: 1.5 }}>{n.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 12, background: T.ink, border: `1px solid ${T.slate}` }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>🛡️</span>
            <div>
              <p style={{ fontSize: 12, fontWeight: 800, color: T.lime, marginBottom: 3 }}>Safety First</p>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', lineHeight: 1.5 }}>
                Always follow traffic rules and wear protective gear while riding.
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, background: T.surface, border: `1px solid ${T.line}` }}>
          <p style={{ fontSize: 12, fontWeight: 800, color: T.ink, marginBottom: 2 }}>Need Help?</p>
          <p style={{ fontSize: 11, color: T.muted }}>Contact our support team 24/7 for any queries or assistance.</p>
        </div>
      </div>
    </Card>
  );
}

// ── PriceSummary ──────────────────────────────────────────────────────────
function PriceSummary({ totals, priceLoading, estimatedPrice }) {
  const Row = ({ label, value, bold, accent, sub }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${T.line}` }}>
      <div>
        <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: T.slate }}>{label}</span>
        {sub && <p style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{sub}</p>}
      </div>
      <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 600, color: accent || T.ink }}>{value}</span>
    </div>
  );

  return (
    <Card>
      {/* Dark header */}
      <div style={{ padding: '16px 20px', background: T.ink }}>
        <p style={{ fontSize: 10, fontWeight: 800, color: T.lime, letterSpacing: 1.8, textTransform: 'uppercase', marginBottom: 4 }}>Price Summary</p>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>All amounts in INR</p>
      </div>
      <div style={{ padding: '4px 20px 20px' }}>
        {priceLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${T.lime}`, borderTopColor: 'transparent', animation: 'spin .8s linear infinite' }} />
          </div>
        ) : (
          <>
            <Row label="Rental Charges" value={`₹${totals.subtotal.toLocaleString()}`} />
            {totals.couponDiscount > 0 && (
              <>
                <Row label="🎉 Coupon Discount" value={`-₹${totals.couponDiscount.toLocaleString()}`} accent={T.teal} bold />
                <Row label="Subtotal after discount" value={`₹${totals.discountedSubtotal.toLocaleString()}`} />
              </>
            )}
            <Row
              label="GST (5%)"
              sub={`on ₹${(totals.couponDiscount > 0 ? totals.discountedSubtotal : totals.subtotal).toLocaleString()}`}
              value={`₹${totals.gst.toLocaleString()}`}
            />
            {/* Total box */}
            <div style={{ marginTop: 16, padding: '16px', borderRadius: 14, background: T.ink, border: `2px solid ${T.lime}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 10, fontWeight: 800, color: T.lime, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 3 }}>Total Amount</p>
              </div>
              <p style={{ fontSize: 26, fontWeight: 900, color: T.lime }}>₹{totals.total.toLocaleString()}</p>
            </div>

            {estimatedPrice?.isPackagePrice && (
              <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(200,242,60,.07)', border: '1px solid rgba(200,242,60,.2)' }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.lime }}>✨ Package Rate Applied</p>
                <p style={{ fontSize: 12, fontWeight: 600, color: T.slate, marginTop: 2 }}>{estimatedPrice.packageName}</p>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ── CouponSection ─────────────────────────────────────────────────────────
function CouponSection({ couponCode, setCouponCode, appliedCoupon, onApply, onRemove, isLoading }) {
  return (
    <Card>
      <CardHead icon="🎟️" title="Apply Coupon" />
      <div style={{ padding: '16px 20px' }}>
        {appliedCoupon ? (
          <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(0,191,165,.08)', border: `1.5px solid ${T.teal}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.teal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>✓</div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 800, color: T.ink }}>{appliedCoupon.code}</p>
                <p style={{ fontSize: 12, color: T.teal, fontWeight: 700 }}>Saved ₹{appliedCoupon.discount.toLocaleString()} 🎉</p>
              </div>
            </div>
            <button onClick={onRemove}
              style={{ padding: '5px 12px', borderRadius: 8, background: 'rgba(255,109,58,.1)', border: '1px solid rgba(255,109,58,.3)', color: T.coral, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Remove
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code" disabled={isLoading}
                style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.line}`, fontSize: 13, fontWeight: 600, color: T.ink, background: T.surface, outline: 'none' }}
                onFocus={e => { e.target.style.borderColor = T.lime; }}
                onBlur={e => { e.target.style.borderColor = T.line; }}
              />
              <button onClick={onApply} disabled={isLoading || !couponCode.trim()}
                style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: isLoading || !couponCode.trim() ? T.line : T.ink, color: isLoading || !couponCode.trim() ? T.muted : T.lime, fontSize: 13, fontWeight: 800, cursor: isLoading || !couponCode.trim() ? 'not-allowed' : 'pointer' }}>
                {isLoading ? '…' : 'Apply'}
              </button>
            </div>
            <p style={{ marginTop: 10, fontSize: 11, color: T.muted }}>
              ℹ️ Have a coupon? Enter it above to save more on your booking.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

// ── PaymentMethodSelector ─────────────────────────────────────────────────
const PAYMENT_METHODS = [
  { id: 'online', label: 'Pay Online via PayU', desc: 'UPI, Cards, Net Banking', emoji: '💳', methods: ['UPI','Debit Card','Credit Card','Net Banking','Wallets'], note: "You'll be redirected to PayU's secure page to complete payment." },
];

function PaymentMethodSelector({ paymentMethod, setPaymentMethod }) {
  return (
    <Card>
      <CardHead icon="💰" title="Payment Method" />
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {PAYMENT_METHODS.map(m => {
          const sel = paymentMethod === m.id;
          return (
            <button key={m.id} onClick={() => setPaymentMethod(m.id)}
              style={{
                padding: '14px 16px', borderRadius: 14, border: `1.5px solid ${sel ? T.lime : T.line}`,
                background: sel ? T.ink : T.surface,
                cursor: 'pointer', textAlign: 'left', transition: 'all .2s',
                boxShadow: sel ? '0 4px 20px rgba(13,13,15,.12)' : 'none',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Radio */}
                <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${sel ? T.lime : T.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'border-color .2s' }}>
                  {sel && <div style={{ width: 9, height: 9, borderRadius: '50%', background: T.lime }} />}
                </div>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{m.emoji}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 800, color: sel ? T.lime : T.ink, marginBottom: 2 }}>{m.label}</p>
                  <p style={{ fontSize: 11, color: sel ? 'rgba(200,242,60,.6)' : T.muted }}>{m.desc}</p>
                </div>
                {sel && <div style={{ fontSize: 14, color: T.lime }}>✓</div>}
              </div>

              {/* Accepted methods row */}
              {sel && m.methods && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid rgba(200,242,60,.2)`, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {m.methods.map(pm => (
                    <span key={pm} style={{ padding: '3px 10px', borderRadius: 16, background: 'rgba(200,242,60,.12)', color: T.lime, fontSize: 10, fontWeight: 700 }}>{pm}</span>
                  ))}
                </div>
              )}

              {/* PayU redirect note */}
              {sel && m.note && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid rgba(200,242,60,.2)`, fontSize: 11, color: 'rgba(200,242,60,.6)' }}>
                  🔒 {m.note}
                </div>
              )}
            </button>
          );
        })}

        {/* Security note */}
        <div style={{ padding: '10px 14px', borderRadius: 10, background: T.surface, border: `1px solid ${T.line}`, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>🔒</span>
          <div>
            <p style={{ fontSize: 12, fontWeight: 800, color: T.ink, marginBottom: 2 }}>Secure Payment</p>
            <p style={{ fontSize: 11, color: T.muted, lineHeight: 1.5 }}>Your payment info is encrypted & processed securely. We never store card details.</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Main Checkout Page ────────────────────────────────────────────────────
export default function RentalCheckout() {
  const navigate = useNavigate();

  const [showAlert,    setShowAlert]    = useState(false);
  const [alertMsg,     setAlertMsg]     = useState('');
  const [alertType,    setAlertType]    = useState('success');
  const [errDetails,   setErrDetails]   = useState(null);

  const notify = (msg, type = 'success', details = null) => {
    setAlertMsg(msg); setAlertType(type); setErrDetails(details); setShowAlert(true);
    setTimeout(() => { setShowAlert(false); setErrDetails(null); }, details ? 6000 : 4000);
  };

  const { bookingData, durationInfo, isValidBooking, estimatedPrice, priceLoading, totals, couponDiscount, setCouponDiscount, authCheckComplete } = useCheckoutData();
  const { couponCode, setCouponCode, appliedCoupon, applyCouponCode, removeCoupon, isLoading: couponLoading } = useCoupon(totals, bookingData, setCouponDiscount, notify);
  const { paymentMethod, setPaymentMethod, termsAccepted, setTermsAccepted, isLoading: paymentLoading, onSubmit } = usePayment(bookingData, durationInfo, totals, isValidBooking, estimatedPrice, notify);

  // Loading
  if (!authCheckComplete) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', border: `3px solid ${T.lime}`, borderTopColor: 'transparent', animation: 'spin .8s linear infinite', margin: '0 auto 14px' }} />
          <p style={{ fontSize: 14, fontWeight: 700, color: T.muted }}>Loading…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
        </div>
      </div>
    );
  }

  // Invalid booking
  if (!isValidBooking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ textAlign: 'center', maxWidth: 380, padding: '0 24px' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: T.ink, marginBottom: 8 }}>Invalid Checkout</h2>
          <p style={{ fontSize: 14, color: T.muted, marginBottom: 24 }}>Booking information is missing or invalid.</p>
          <button onClick={() => navigate(ROUTES.RENTAL)}
            style={{ padding: '12px 28px', borderRadius: 12, background: T.ink, color: T.lime, border: 'none', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            Return to Search →
          </button>
        </div>
      </div>
    );
  }

  const canSubmit = !paymentLoading && paymentMethod && termsAccepted;

  return (
    <div style={{ minHeight: '100vh', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&family=Space+Grotesk:wght@700&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:.35;} }
        @keyframes fadeUp{ from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:none;} }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: ${T.line}; border-radius: 4px; }
      `}</style>

      <AlertNotification show={showAlert} message={alertMsg} type={alertType} errorDetails={errDetails} onClose={() => { setShowAlert(false); setErrDetails(null); }} />

      {/* ── HEADER ── */}
      <div style={{ background: T.ink, position: 'sticky', top: 0, zIndex: 50, borderBottom: `1px solid ${T.slate}` }}>
        {/* lime top bar */}
        <div style={{ height: 2, background: T.lime }} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 62 }}>
            {/* Back + Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button onClick={() => navigate(-1)}
                style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.slate}`, background: 'none', color: T.white, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ←
              </button>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(200,242,60,.1)', border: '1px solid rgba(200,242,60,.25)', borderRadius: 20, padding: '2px 10px', marginBottom: 2 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.lime, display: 'inline-block' }} />
                  <span style={{ fontSize: 9, fontWeight: 800, color: T.lime, letterSpacing: 1.2, textTransform: 'uppercase' }}>Almost There</span>
                </div>
                <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 17, fontWeight: 700, color: T.white }}>
                  🔒 Secure Checkout
                </h1>
              </div>
            </div>

            {/* Secure badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 14px', borderRadius: 20, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)' }}>
              <span style={{ fontSize: 13 }}>🛡️</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.6)' }}>100% Secure</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 20px 60px' }}>
        <form onSubmit={e => onSubmit(e, appliedCoupon)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>

            {/* ── LEFT COLUMN ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <VehicleCard bookingData={bookingData} />
              <TripDetailsCard bookingData={bookingData} durationInfo={durationInfo} />
              <ImportantNotes />
            </div>

            {/* ── RIGHT COLUMN ── */}
            <div style={{ position: 'sticky', top: 80, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <PriceSummary totals={totals} priceLoading={priceLoading} estimatedPrice={estimatedPrice} />

              <CouponSection
                couponCode={couponCode} setCouponCode={setCouponCode}
                appliedCoupon={appliedCoupon} onApply={applyCouponCode}
                onRemove={removeCoupon} isLoading={couponLoading}
              />

              <PaymentMethodSelector paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} />

              {/* Terms */}
              <Card>
                <div style={{ padding: '16px 20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                    <div style={{ position: 'relative', flexShrink: 0, marginTop: 1 }}>
                      <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                        style={{ width: 18, height: 18, accentColor: T.lime, cursor: 'pointer' }} />
                    </div>
                    <span style={{ fontSize: 13, color: T.slate, lineHeight: 1.6 }}>
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: T.ink, fontWeight: 800, textDecoration: 'underline' }}>Terms & Conditions</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: T.ink, fontWeight: 800, textDecoration: 'underline' }}>Privacy Policy</a>
                    </span>
                  </label>
                </div>
              </Card>

              {/* Submit CTA */}
              <button type="submit" disabled={!canSubmit}
                onMouseEnter={e => { if (canSubmit) e.currentTarget.style.opacity = '.88'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                style={{
                  width: '100%', padding: '16px', borderRadius: 14, border: 'none',
                  background: canSubmit ? T.ink : T.line,
                  color: canSubmit ? T.lime : T.muted,
                  fontSize: 15, fontWeight: 900, cursor: canSubmit ? 'pointer' : 'not-allowed',
                  letterSpacing: .4, transition: 'opacity .2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: canSubmit ? '0 8px 28px rgba(13,13,15,.2)' : 'none',
                }}>
                {paymentLoading ? (
                  <>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${T.lime}`, borderTopColor: 'transparent', animation: 'spin .7s linear infinite' }} />
                    Processing…
                  </>
                ) : (
                  <>🔒 Confirm & Pay ₹{totals.total.toLocaleString()}</>
                )}
              </button>

              {/* Security badge */}
              <div style={{ padding: '12px 16px', borderRadius: 12, background: T.white, border: `1px solid ${T.line}`, textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 15 }}>🔒</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>Secure Payment</span>
                </div>
                <p style={{ fontSize: 11, color: T.muted }}>Your payment is encrypted & 100% safe</p>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Responsive: stack columns on mobile */}
      <style>{`
        @media(max-width:900px){
          form > div { grid-template-columns: 1fr !important; }
          form > div > div:last-child { position: static !important; }
        }
      `}</style>
    </div>
  );
}
